// Helpers for switching the accommodation terminology between hotel-style
// "rooms" and bivouac-style "tents" depending on the property slug.
//
// Used across hero, pricing strip, room/tent service pricing tab, the
// pricing dialog inside PropertyCardActions, the "Room Only" board plan
// label and any other surface that surfaces user-facing language.

// Lazy require so propertyType.js stays a tiny pure helper module —
// importing hotelDetails eagerly would create a circular dep on
// downstream consumers that already import propertyType.
let _hotelDetailsFn = null;
const getDetailsFn = () => {
  if (_hotelDetailsFn) return _hotelDetailsFn;
  _hotelDetailsFn = require("./hotelDetails").getHotelDetails;
  return _hotelDetailsFn;
};

// Hard-coded list because the property catalogue is small and stable.
// Easier and safer than parsing slugs heuristically.
const BIVOUAC_SLUGS = new Set([
  "bivouacs-xaluca",
  "la-belle-etoile-bivouacs",
  "kamkamia-luxury-desert-camp",
  "merdani-bivouac",
]);

export const isBivouac = (slug) =>
  !!slug && (BIVOUAC_SLUGS.has(slug) || /bivouac|desert-camp/i.test(slug));

// Returns a small bag of trilingual labels swapped to match the property
// type. `slug` is the hotel/bivouac slug, `lang` the active language.
//
// Bivouacs use the universal Berber/Arabic term "haima" (jaima) across
// every language — the word is understood by international travellers,
// evokes the desert immediately and avoids the generic hotel-industry
// "tent". Hotels keep the traditional room/chambre/habitación wording.
export const getAccomTerms = (slug, lang = "en") => {
  const bivouac = isBivouac(slug);
  const en = bivouac
    ? {
        unit: "haima", units: "haimas", Unit: "Haima", Units: "Haimas",
        room_only: "Haima Only", rates_title: "Haima Rates", public_rates: "Public haima rates",
        section_title: "Haimas & Suites",
        section_intro: "Four haima categories — from traditional Sahrawi haimas to en-suite luxury haimas, each pitched to a different way of spending a night in the Sahara.",
        book_cta: "Book this haima",
        stat_label: "Haimas & camp",
      }
    : {
        unit: "room", units: "rooms", Unit: "Room", Units: "Rooms",
        room_only: "Room Only", rates_title: "Room Rates", public_rates: "Public room rates",
        section_title: "Rooms & Suites",
        section_intro: "Four categories, each handcrafted with Berber textures and modern serenity.",
        book_cta: "Book this room",
        stat_label: "Rooms & suites",
      };
  const fr = bivouac
    ? {
        unit: "haima", units: "haimas", Unit: "Haima", Units: "Haimas",
        room_only: "Haima seule", rates_title: "Tarifs des haimas", public_rates: "Tarifs publics des haimas",
        section_title: "Haimas & Suites",
        section_intro: "Quatre catégories de haimas — de la haima sahraouie traditionnelle à la haima-suite en toile blanche, chacune pensée pour une façon différente de vivre la nuit du désert.",
        book_cta: "Réserver cette haima",
        stat_label: "Haimas & camp",
      }
    : {
        unit: "chambre", units: "chambres", Unit: "Chambre", Units: "Chambres",
        room_only: "Chambre seule", rates_title: "Tarifs des chambres", public_rates: "Tarifs publics des chambres",
        section_title: "Chambres & Suites",
        section_intro: "Quatre catégories, chacune façonnée à la main entre textures berbères et sérénité moderne.",
        book_cta: "Réserver cette chambre",
        stat_label: "Chambres & suites",
      };
  const es = bivouac
    ? {
        unit: "haima", units: "haimas", Unit: "Haima", Units: "Haimas",
        room_only: "Solo haima", rates_title: "Tarifas de haimas", public_rates: "Tarifas públicas de haimas",
        section_title: "Haimas y Suites",
        section_intro: "Cuatro tipos de haima — desde la haima saharaui tradicional hasta la haima-suite en lona blanca, cada una pensada para una manera distinta de vivir la noche del desierto.",
        book_cta: "Reservar esta haima",
        stat_label: "Haimas y campamento",
      }
    : {
        unit: "habitación", units: "habitaciones", Unit: "Habitación", Units: "Habitaciones",
        room_only: "Solo habitación", rates_title: "Tarifas de habitaciones", public_rates: "Tarifas públicas de habitaciones",
        section_title: "Habitaciones y Suites",
        section_intro: "Cuatro categorías, cada una creada a mano entre texturas bereberes y serenidad moderna.",
        book_cta: "Reservar esta habitación",
        stat_label: "Habitaciones y suites",
      };
  const bag = { en, fr, es };
  return bag[lang] || bag.en;
};

// Returns the array of accommodation types defined for a property —
// `[{ key, name: {en, fr, es}, capacity, surface }]`. `key` is the
// English name lowercased (stable id used as the select value and
// persisted through URL params and the inquiry payload).
//
// Falls back to the DEFAULT_ROOMS / DEFAULT_TENTS catalogues when the
// specific property override doesn't define rooms — so every hotel
// always exposes at least the brand-standard inventory.
export const getAccommodationTypes = (slug) => {
  if (!slug) return [];
  try {
    const details = getDetailsFn()(slug);
    const inv = Array.isArray(details?.rooms) ? details.rooms : [];
    return inv.map((r) => {
      const en = (r?.name?.en || r?.name?.fr || r?.name?.es || "").trim();
      return {
        key: en.toLowerCase().replace(/\s+/g, "-") || `room-${Math.random().toString(36).slice(2, 6)}`,
        name: r.name || { en, fr: en, es: en },
        capacity: r.capacity || null,
        surface: r.surface || null,
      };
    });
  } catch {
    return [];
  }
};

// Resolve a stored room-type value (free-text name OR our stable key)
// back to the matching entry in `getAccommodationTypes(slug)`. Returns
// null when nothing matches — UI can then keep showing the raw text.
export const findAccommodationType = (slug, stored) => {
  if (!stored) return null;
  const list = getAccommodationTypes(slug);
  if (!list.length) return null;
  const q = stored.toString().trim().toLowerCase();
  return list.find((opt) => {
    if (opt.key === q) return true;
    return ["en", "fr", "es"].some((lng) => (opt.name?.[lng] || "").toLowerCase() === q);
  }) || null;
};
